/**
 * Timezone Helper Utilities
 * Handles timezone conversions and smart scheduling for diaspora clients
 * Uses native JavaScript Intl API - no external dependencies required
 */

// Common diaspora locations with their timezones
export const DIASPORA_TIMEZONES = {
    'United States - East Coast': 'America/New_York',
    'United States - West Coast': 'America/Los_Angeles',
    'United States - Central': 'America/Chicago',
    'United Kingdom': 'Europe/London',
    'South Africa': 'Africa/Johannesburg',
    'Australia - Sydney': 'Australia/Sydney',
    'Canada - Toronto': 'America/Toronto',
    'Canada - Vancouver': 'America/Vancouver',
    'Zimbabwe': 'Africa/Harare',
    'Botswana': 'Africa/Gaborone',
    'Zambia': 'Africa/Lusaka'
};

// Zimbabwe timezone constant
export const ZIMBABWE_TIMEZONE = 'Africa/Harare';

/**
 * Get user's current timezone
 */
export const getUserTimezone = () => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Format date in specific timezone
 */
export const formatInTimezone = (date, timezone, options = {}) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    const defaultOptions = {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZone: timezone,
        ...options
    };
    
    return new Intl.DateTimeFormat('en-US', defaultOptions).format(dateObj);
};

/**
 * Get hour in specific timezone
 */
export const getHourInTimezone = (date, timezone) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const options = { hour: 'numeric', hour12: false, timeZone: timezone };
    return parseInt(new Intl.DateTimeFormat('en-US', options).format(dateObj));
};

/**
 * Get day of week in specific timezone (0 = Sunday, 6 = Saturday)
 */
export const getDayInTimezone = (date, timezone) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    // Convert to locale string in that timezone and parse
    const str = dateObj.toLocaleString('en-US', { timeZone: timezone, weekday: 'short' });
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return days.indexOf(str);
};

/**
 * Get time difference between two timezones in hours
 */
export const getTimeDifference = (timezone1, timezone2) => {
    const now = new Date();
    
    // Get offset for each timezone
    const getOffset = (tz) => {
        const tzDate = new Date(now.toLocaleString('en-US', { timeZone: tz }));
        const utcDate = new Date(now.toLocaleString('en-US', { timeZone: 'UTC' }));
        return (tzDate - utcDate) / (1000 * 60 * 60);
    };
    
    return getOffset(timezone1) - getOffset(timezone2);
};

/**
 * Format time in both user timezone and Zimbabwe timezone
 */
export const formatDualTimezone = (date, userTimezone = getUserTimezone()) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    const userTime = formatInTimezone(dateObj, userTimezone);
    const zimbabweTime = formatInTimezone(dateObj, ZIMBABWE_TIMEZONE);
    
    // Get timezone abbreviations
    const userTzShort = new Intl.DateTimeFormat('en-US', {
        timeZone: userTimezone,
        timeZoneName: 'short'
    }).formatToParts(dateObj).find(part => part.type === 'timeZoneName')?.value || '';
    
    const zimTzShort = new Intl.DateTimeFormat('en-US', {
        timeZone: ZIMBABWE_TIMEZONE,
        timeZoneName: 'short'
    }).formatToParts(dateObj).find(part => part.type === 'timeZoneName')?.value || '';
    
    return {
        userTime: `${userTime} ${userTzShort}`,
        zimbabweTime: `${zimbabweTime} ${zimTzShort}`,
        userTimezone,
        zimbabweTimezone: ZIMBABWE_TIMEZONE,
        timeDiff: Math.round(getTimeDifference(userTimezone, ZIMBABWE_TIMEZONE))
    };
};

/**
 * Check if time is within business hours in Zimbabwe
 */
export const isZimbabweBusinessHours = (date = new Date()) => {
    const hour = getHourInTimezone(date, ZIMBABWE_TIMEZONE);
    const day = getDayInTimezone(date, ZIMBABWE_TIMEZONE);
    
    // Monday-Friday (1-5), 8 AM - 5 PM
    return day >= 1 && day <= 5 && hour >= 8 && hour < 17;
};

/**
 * Get next business hour in Zimbabwe
 */
export const getNextZimbabweBusinessHour = (date = new Date()) => {
    let checkDate = new Date(date);
    const maxIterations = 168; // 1 week
    let iterations = 0;
    
    while (!isZimbabweBusinessHours(checkDate) && iterations < maxIterations) {
        checkDate = new Date(checkDate.getTime() + 60 * 60 * 1000); // Add 1 hour
        iterations++;
    }
    
    return checkDate;
};

/**
 * Suggest best time for communication (when both parties likely awake)
 */
export const suggestBestCommunicationTime = (userTimezone = getUserTimezone()) => {
    const now = new Date();
    const suggestions = [];
    
    // Check next 48 hours for good windows
    for (let i = 0; i < 48; i++) {
        const checkTime = new Date(now.getTime() + i * 60 * 60 * 1000);
        const userHour = getHourInTimezone(checkTime, userTimezone);
        const zimHour = getHourInTimezone(checkTime, ZIMBABWE_TIMEZONE);
        const zimDay = getDayInTimezone(checkTime, ZIMBABWE_TIMEZONE);
        
        // Good window: 8 AM - 10 PM for user, 8 AM - 5 PM for Zimbabwe (business hours)
        if (userHour >= 8 && userHour < 22 && zimHour >= 8 && zimHour < 17 && zimDay >= 1 && zimDay <= 5) {
            suggestions.push({
                timestamp: checkTime,
                userTime: formatInTimezone(checkTime, userTimezone, { weekday: 'short' }),
                zimbabweTime: formatInTimezone(checkTime, ZIMBABWE_TIMEZONE, { weekday: 'short' }),
                quality: userHour >= 9 && userHour < 18 ? 'excellent' : 'good'
            });
            
            // Return top 3 suggestions
            if (suggestions.length >= 3) break;
        }
    }
    
    return suggestions;
};

/**
 * Format relative time with timezone awareness
 */
export const formatRelativeTimeWithTZ = (date, userTimezone = getUserTimezone()) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const now = new Date();
    const diffInHours = Math.floor((now - dateObj) / (1000 * 60 * 60));
    
    let relative;
    if (diffInHours < 1) {
        const diffInMinutes = Math.floor((now - dateObj) / (1000 * 60));
        relative = diffInMinutes < 1 ? 'Just now' : `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
    } else if (diffInHours < 24) {
        relative = `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    } else {
        const days = Math.floor(diffInHours / 24);
        relative = `${days} day${days > 1 ? 's' : ''} ago`;
    }
    
    const userTime = formatInTimezone(dateObj, userTimezone);
    
    return {
        relative,
        absolute: userTime,
        timezone: userTimezone
    };
};

/**
 * Convert Zimbabwe time to user's local time
 */
export const zimbabweToUserTime = (zimbabweDate, userTimezone = getUserTimezone()) => {
    const dateObj = typeof zimbabweDate === 'string' ? new Date(zimbabweDate) : zimbabweDate;
    return formatInTimezone(dateObj, userTimezone);
};

/**
 * Schedule message for optimal delivery time
 */
export const getOptimalMessageTime = (urgency = 'normal', userTimezone = getUserTimezone()) => {
    const now = new Date();
    
    switch (urgency) {
        case 'urgent':
            // Send immediately
            return now;
            
        case 'normal':
            // Send during next business hours if outside business hours
            if (!isZimbabweBusinessHours(now)) {
                return getNextZimbabweBusinessHour(now);
            }
            return now;
            
        case 'low':
            // Send during next optimal communication window
            const suggestions = suggestBestCommunicationTime(userTimezone);
            return suggestions.length > 0 ? suggestions[0].timestamp : now;
            
        default:
            return now;
    }
};

/**
 * Get timezone-friendly greeting
 */
export const getTimezoneGreeting = (userTimezone = getUserTimezone()) => {
    const hour = getHourInTimezone(new Date(), userTimezone);
    
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
};

/**
 * Check if times overlap for scheduling meetings
 */
export const checkTimezoneOverlap = (startTime, endTime, userTimezone = getUserTimezone()) => {
    const start = typeof startTime === 'string' ? new Date(startTime) : startTime;
    const end = typeof endTime === 'string' ? new Date(endTime) : endTime;
    
    const userStartHour = getHourInTimezone(start, userTimezone);
    const userEndHour = getHourInTimezone(end, userTimezone);
    const zimStartHour = getHourInTimezone(start, ZIMBABWE_TIMEZONE);
    const zimEndHour = getHourInTimezone(end, ZIMBABWE_TIMEZONE);
    
    // Check if both parties are in reasonable hours (8 AM - 10 PM for user, 8 AM - 5 PM for Zimbabwe)
    const userReasonable = userStartHour >= 8 && userEndHour <= 22;
    const zimBusinessHours = zimStartHour >= 8 && zimEndHour <= 17;
    
    return {
        suitable: userReasonable && zimBusinessHours,
        userReasonable,
        zimBusinessHours,
        reason: !userReasonable ? 'Outside your reasonable hours' : 
                !zimBusinessHours ? 'Outside Zimbabwe business hours' : 
                'Good time for both parties'
    };
};

export default {
    DIASPORA_TIMEZONES,
    ZIMBABWE_TIMEZONE,
    getUserTimezone,
    formatInTimezone,
    getTimeDifference,
    formatDualTimezone,
    isZimbabweBusinessHours,
    getNextZimbabweBusinessHour,
    suggestBestCommunicationTime,
    formatRelativeTimeWithTZ,
    zimbabweToUserTime,
    getOptimalMessageTime,
    getTimezoneGreeting,
    checkTimezoneOverlap
};
